/** `snippets/product-info.liquid` — `[data-product-info]`: variant-driven price
 *  updates, quantity stepper, subscribe vs buy-once (selling plan hidden input),
 *  accordion panels, and region-based shipping availability on the product meta column.
 */

const REGION_LS_KEY = 'wyld_us_state'

const ROOT_SELECTOR = '[data-product-info]'
const VARIANT_SELECT_SELECTOR = '[data-product-info-variant-select]'
const QTY_INPUT_SELECTOR = '[data-product-info-qty]'
const QTY_MINUS_SELECTOR = '[data-product-info-qty-minus]'
const QTY_PLUS_SELECTOR = '[data-product-info-qty-plus]'
const QTY_DISPLAY_SELECTOR = '[data-product-info-qty-display]'
const PRICE_SELECTOR = '[data-product-info-price]'
const PACK_LINE_SELECTOR = '[data-product-info-pack-line]'
const SELLING_PLAN_SELECTOR = '[data-product-info-selling-plan]'
const PURCHASE_RADIO_SELECTOR = '[data-product-info-purchase-radio]'
const PURCHASE_ONCE_SELECTOR = '[data-product-info-purchase-once]'
const PURCHASE_SUBSCRIBE_SELECTOR = '[data-product-info-purchase-subscribe]'
const ONCE_PRICE_SELECTOR = '[data-product-info-once-price]'
const SUBSCRIBE_COMPARE_SELECTOR = '[data-product-info-subscribe-compare]'
const SUBSCRIBE_PRICE_SELECTOR = '[data-product-info-subscribe-price]'
const SUBSCRIBE_BADGE_SELECTOR = '[data-product-info-subscribe-badge]'
const ADD_BTN_SELECTOR = '[data-product-info-add-btn]'
const ACCORDION_TRIGGER_SELECTOR = '[data-product-info-accordion-trigger]'
const FORM_SELECTOR = '[data-ajax-product-form]'
const LOCATOR_SELECTOR = '[data-product-info-locator]'
const AVAILABILITY_SELECTOR = '[data-product-availability]'

function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(Number(cents) / 100)
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || ''}`.trim()
  }
}

function parseVariantPayload(root) {
  const script = root.querySelector('script[type="application/json"]')
  if (!script?.textContent) return { variants: [] }
  try {
    return JSON.parse(script.textContent)
  } catch {
    return { variants: [] }
  }
}

function initProductInfo(root) {
  const currency = root.dataset.currency || 'USD'
  const { variants } = parseVariantPayload(root)
  if (!variants.length) return

  const variantSelect = root.querySelector(VARIANT_SELECT_SELECTOR)
  const qtyInput = root.querySelector(QTY_INPUT_SELECTOR)
  const qtyMinus = root.querySelector(QTY_MINUS_SELECTOR)
  const qtyPlus = root.querySelector(QTY_PLUS_SELECTOR)
  const qtyDisplay = root.querySelector(QTY_DISPLAY_SELECTOR)
  const priceEl = root.querySelector(PRICE_SELECTOR)
  const packLineEl = root.querySelector(PACK_LINE_SELECTOR)
  const sellingPlanInput = root.querySelector(SELLING_PLAN_SELECTOR)
  const purchaseRadios = root.querySelectorAll(PURCHASE_RADIO_SELECTOR)
  const onceLabel = root.querySelector(PURCHASE_ONCE_SELECTOR)
  const subscribeLabel = root.querySelector(PURCHASE_SUBSCRIBE_SELECTOR)
  const oncePriceEl = root.querySelector(ONCE_PRICE_SELECTOR)
  const subscribeCompareEl = root.querySelector(SUBSCRIBE_COMPARE_SELECTOR)
  const subscribePriceEl = root.querySelector(SUBSCRIBE_PRICE_SELECTOR)
  const subscribeBadgeEl = root.querySelector(SUBSCRIBE_BADGE_SELECTOR)
  const addBtn = root.querySelector(ADD_BTN_SELECTOR)

  const getVariantId = () => {
    if (variantSelect) return Number(variantSelect.value, 10)
    const hiddenId = root.querySelector('form.product-info-form input[name="id"]')
    return Number(hiddenId?.value, 10)
  }

  const findVariant = (id) => variants.find((v) => v.id === id)

  const syncSellingPlanFromPurchaseMode = () => {
    if (!sellingPlanInput) return
    const selected = root.querySelector(`${PURCHASE_RADIO_SELECTOR}:checked`)
    const mode = selected?.value
    const id = getVariantId()
    const variant = findVariant(id)
    if (mode === 'subscribe' && variant?.allocations?.length) {
      sellingPlanInput.value = String(variant.allocations[0].plan_id)
    } else {
      sellingPlanInput.value = ''
    }
  }

  const setPurchaseActiveClass = () => {
    const selected = root.querySelector(`${PURCHASE_RADIO_SELECTOR}:checked`)
    const mode = selected?.value
    onceLabel?.classList.toggle('product-info-purchase-option-active', mode === 'once')
    subscribeLabel?.classList.toggle('product-info-purchase-option-active', mode === 'subscribe')
  }

  const updateSubscribeBadge = (variant) => {
    if (!subscribeBadgeEl) return
    const alloc = variant?.allocations?.[0]
    if (!alloc || !variant?.price) {
      subscribeBadgeEl.hidden = true
      subscribeBadgeEl.textContent = ''
      return
    }
    const diff = variant.price - alloc.price
    if (diff <= 0) {
      subscribeBadgeEl.hidden = true
      subscribeBadgeEl.textContent = ''
      return
    }
    const pct = Math.round((diff / variant.price) * 100)
    subscribeBadgeEl.hidden = false
    subscribeBadgeEl.textContent = `save ${pct}%`
  }

  const applyVariant = (variantId) => {
    const variant = findVariant(variantId)
    if (!variant) return

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price, currency)
    }
    if (packLineEl) {
      const unitQuantity = variant.unit_quantity || ''
      packLineEl.textContent = unitQuantity
      packLineEl.classList.toggle('product-info-pack-line-placeholder', !unitQuantity)
    }
    if (oncePriceEl) {
      oncePriceEl.textContent = formatMoney(variant.price, currency)
    }
    if (subscribeCompareEl) {
      subscribeCompareEl.textContent = formatMoney(variant.price, currency)
    }
    const alloc = variant.allocations?.[0]
    if (subscribePriceEl) {
      subscribePriceEl.textContent = alloc ? formatMoney(alloc.price, currency) : ''
    }

    updateSubscribeBadge(variant)

    updateAddButton()

    const subscribeRadio = root.querySelector(
      `${PURCHASE_RADIO_SELECTOR}[value="subscribe"]`,
    )
    if (subscribeRadio && !alloc) {
      subscribeRadio.checked = false
      const onceRadio = root.querySelector(`${PURCHASE_RADIO_SELECTOR}[value="once"]`)
      if (onceRadio) onceRadio.checked = true
    }

    syncSellingPlanFromPurchaseMode()
    setPurchaseActiveClass()
  }

  const dispatchVariantChanged = (variantId) => {
    const variant = findVariant(variantId)
    document.dispatchEvent(
      new CustomEvent('theme:variant:changed', {
        detail: { variantId, mediaId: variant?.featured_media_id ?? null },
      }),
    )
  }

  if (variantSelect) {
    variantSelect.addEventListener('change', () => {
      const variantId = Number(variantSelect.value, 10)
      applyVariant(variantId)
      dispatchVariantChanged(variantId)
    })
  }

  purchaseRadios.forEach((radio) => {
    radio.addEventListener('change', () => {
      syncSellingPlanFromPurchaseMode()
      setPurchaseActiveClass()
    })
  })

  const syncQtyDisplay = () => {
    const n = Math.max(1, Math.min(99, Number(qtyInput?.value || 1, 10)))
    if (qtyInput) qtyInput.value = String(n)
    if (qtyDisplay) qtyDisplay.textContent = String(n)
  }

  qtyMinus?.addEventListener('click', () => {
    if (!qtyInput) return
    const n = Number(qtyInput.value || 1, 10)
    qtyInput.value = String(Math.max(1, n - 1))
    syncQtyDisplay()
  })

  qtyPlus?.addEventListener('click', () => {
    if (!qtyInput) return
    const n = Number(qtyInput.value || 1, 10)
    qtyInput.value = String(Math.min(99, n + 1))
    syncQtyDisplay()
  })

  syncQtyDisplay()

  root.querySelectorAll(ACCORDION_TRIGGER_SELECTOR).forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true'
      const panelId = trigger.getAttribute('aria-controls')
      const panel = panelId ? document.getElementById(panelId) : null
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true')
      if (panel) togglePanel(panel, !expanded)
    })
  })

  // ── Region-based shipping availability ─────────────────────────────
  const formEl = root.querySelector(FORM_SELECTOR)
  const locatorEl = root.querySelector(LOCATOR_SELECTOR)
  const availEl = root.querySelector(AVAILABILITY_SELECTOR)
  const includedStates = availEl ? parseJsonAttr(availEl, 'data-included-states') : []

  let regionKnown = false
  let regionAvailable = true

  const currentVariantAvailable = () => {
    const v = findVariant(getVariantId())
    return v ? v.available : false
  }

  function updateAddButton() {
    if (!addBtn) return
    addBtn.disabled = !currentVariantAvailable() || (regionKnown && !regionAvailable)
  }

  function applyRegion(state) {
    if (!state) {
      regionKnown = false
      regionAvailable = true
      if (formEl) formEl.hidden = false
      if (locatorEl) locatorEl.hidden = true
      if (addBtn) addBtn.disabled = true
      return
    }

    regionKnown = true
    regionAvailable = isAvailableInState(includedStates, state)

    if (!regionAvailable && state === 'California') {
      globalThis.location.href = '/'
      return
    }

    if (formEl) formEl.hidden = !regionAvailable
    if (locatorEl) locatorEl.hidden = regionAvailable

    if (regionAvailable) {
      updateAddButton()
    } else if (addBtn) {
      addBtn.disabled = true
    }
  }

  applyVariant(getVariantId())

  const currentState = safeLocalStorageGet(REGION_LS_KEY)
  applyRegion(currentState)

  // IP-based detection runs in the header (site-wide) and broadcasts the
  // resolved state via `theme:region:state-changed`, which this listener picks up.
  document.addEventListener('theme:region:state-changed', (e) => {
    applyRegion(e.detail?.state || '')
  })
}

function togglePanel(panel, open) {
  if (open) {
    panel.classList.add('product-info-accordion-panel-open')
    // Animate from the current height (usually 0) up to the content height,
    // then release to `auto` so the panel can reflow if its content changes.
    panel.style.height = `${panel.scrollHeight}px`
    const onEnd = (e) => {
      if (e.propertyName !== 'height') return
      panel.style.height = 'auto'
      panel.removeEventListener('transitionend', onEnd)
    }
    panel.addEventListener('transitionend', onEnd)
  } else {
    // Pin the current pixel height (in case it is `auto`) before collapsing to 0
    // so the transition has two explicit endpoints to animate between.
    panel.style.height = `${panel.scrollHeight}px`
    // Force a reflow so the browser registers the pinned height as the start.
    panel.getBoundingClientRect()
    panel.classList.remove('product-info-accordion-panel-open')
    panel.style.height = '0px'
  }
}

function parseJsonAttr(el, attr) {
  const raw = el.getAttribute(attr)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function isAvailableInState(includedStates, state) {
  if (includedStates.length === 0) return true
  return includedStates.includes(state)
}

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function initAllProductInfo() {
  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    initProductInfo(root)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllProductInfo)
} else {
  initAllProductInfo()
}
